import { useEffect, useMemo, useState } from 'react'
import { nf } from '@/shared/util/numbers'
import { loadAnalytics, type AnalyticsSnapshot } from './repository/appAnalyticsRepository'
import { loadAccessGrants, type AccessGrantRow } from './repository/accessGrantsRepository'
import { GrantPeopleModal } from './GrantPeopleModal'
import { PromoCodeForm } from './PromoCodePanel'
import { Modal } from '@/shared/ui/Modal'
import { useAuth } from '@/features/auth/AuthProvider'
import { loadFeedStatus, type FeedStatus } from './repository/subscriptionFeedRepository'
import {
  FLOW_STAGES,
  loadMetrics,
  loadSparklines,
  METRICS,
  type MetricDef,
  type MetricKey,
  type Window,
} from './repository/periodMetricsRepository'
import { Sparkline } from '@/shared/ui/Sparkline'
import {
  loadMirrorFreshness,
  loadRevenueCatOverview,
  type MirrorFreshness,
  type RevenueCatOverview,
} from './repository/revenueCatOverviewRepository'
import { SeasonGoal } from './SeasonGoal'
import { RangeFilter, rangeFor, type Range } from './RangeFilter'
import { grossInRange } from './repository/seasonGoalRepository'
import { InfoTip } from '@/shared/ui/InfoTip'
import { ProductAccessError } from '@/features/auth/ProductAccessError'
import { ProSubscribersCard } from './ProSubscribersCard'
import { PlacesCard } from './PlacesCard'
import { MetricChartModal } from './MetricChartModal'
import { formatDate } from '@/shared/util/dates'
import './ProUsageDashboard.css'


/**
 * Consumer Skida usage.
 *
 * Every number is a server-side aggregation count; no user document is loaded
 * into the browser. Campaign conversion counts the specific accounts that
 * redeemed each code — the user ids build the query and are never rendered.
 */
export function AppAnalytics() {
  // The design's control: seven presets and a custom range, and every figure
  // below reads from it. 28 days rather than 30 is four exact weeks.
  const [range, setRange] = useState<Range>(() => rangeFor('d28'))
  const [gross, setGross] = useState<number | null>(null)
  const [data, setData] = useState<AnalyticsSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [, setLoading] = useState(true)
  const [grants, setGrants] = useState<AccessGrantRow[] | null>(null)
  const [grantsError, setGrantsError] = useState<string | null>(null)
  const [openGrant, setOpenGrant] = useState<AccessGrantRow | null>(null)
  const [creatingCode, setCreatingCode] = useState(false)
  const [grantsVersion, setGrantsVersion] = useState(0)
  const { isAdmin, productEmail } = useAuth()
  const [feed, setFeed] = useState<FeedStatus | null>(null)
  const [metrics, setMetrics] = useState<Record<MetricKey, number | null> | null>(null)
  /** Why a box has no figure. Empty when every query answered. */
  const [metricErrors, setMetricErrors] = useState<Partial<Record<MetricKey, string>>>({})
  /** RevenueCat's own totals, for the trial cross-check below the row. */
  const [rcOverview, setRcOverview] = useState<RevenueCatOverview | null>(null)
  /** How much of the subscription mirror still describes the last full pass. */
  const [mirror, setMirror] = useState<MirrorFreshness | null>(null)
  const [sparks, setSparks] = useState<Record<MetricKey, number[] | null> | null>(null)
  const [openMetric, setOpenMetric] = useState<MetricDef | null>(null)

  useEffect(() => {
    let cancelled = false
    loadFeedStatus()
      .then((f) => {
        if (!cancelled) setFeed(f)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [grantsVersion])

  /**
   * The window every headline number is measured over. `All time` is
   * `from: null`, which drops the lower bound rather than picking an arbitrary
   * epoch — the boxes then genuinely mean "all of them".
   */
  const window: Window = useMemo(
    () => ({ from: range.from, to: range.to }),
    [range],
  )
  const rangeLabel = range.label

  useEffect(() => {
    let cancelled = false
    setMetrics(null)
    setMetricErrors({})
    loadMetrics(window)
      .then((m) => {
        if (cancelled) return
        setMetrics(m.values)
        setMetricErrors(m.errors)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setMetrics(null)
        setMetricErrors({})
        setError(err instanceof Error ? err.message : 'Could not read the metrics.')
      })
    return () => {
      cancelled = true
    }
    // Read again when the product account changes — a reconnect from the
    // error below then shows the figures without a reload.
  }, [window, productEmail])

  // The shapes are a second, slower request rather than part of the first: the
  // number is what the row is read for, and it must not wait on eight buckets
  // per box. The lines appear underneath the figures a moment later.
  useEffect(() => {
    let cancelled = false
    setSparks(null)
    loadSparklines(window)
      .then((s) => {
        if (!cancelled) setSparks(s)
      })
      .catch(() => {
        if (!cancelled) setSparks(null)
      })
    return () => {
      cancelled = true
    }
  }, [window])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadAnalytics(range.from === null ? null : Math.ceil((range.to - range.from) / 86_400_000))
      .then((snapshot) => {
        if (!cancelled) setData(snapshot)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Query failed.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [range, productEmail])

  // Money for the selected range. Its own request rather than part of the
  // analytics snapshot: it reads the CRM's subscription mirror, not the
  // product's users, and the two have nothing else in common.
  useEffect(() => {
    let live = true
    setGross(null)
    grossInRange(range.from, range.to)
      .then((n) => live && setGross(n))
      .catch(() => live && setGross(null))
    return () => {
      live = false
    }
  }, [range])

  // Access grants do not depend on the date range, so they load once.
  useEffect(() => {
    let cancelled = false
    loadAccessGrants()
      .then((rows) => {
        if (cancelled) return
        setGrants(rows)
        // An open code follows its saved version, so the header shows the edit.
        setOpenGrant((open) => (open ? (rows.find((r) => r.kind === open.kind && r.id === open.id) ?? open) : open))
      })
      .catch((err: unknown) => {
        if (!cancelled) setGrantsError(err instanceof Error ? err.message : 'Query failed.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let live = true
    loadRevenueCatOverview()
      .then((o) => live && setRcOverview(o))
      .catch(() => live && setRcOverview(null))
    loadMirrorFreshness()
      .then((m) => live && setMirror(m))
      .catch(() => live && setMirror(null))
    return () => {
      live = false
    }
  }, [])
  const grantTotals = useMemo(() => {
    if (!grants) return null
    return {
      given: grants.reduce((n, g) => n + g.total, 0),
      live: grants.reduce((n, g) => n + g.live, 0),
      used: grants.filter((g) => g.total > 0).length,
    }
  }, [grants])

  return (
    <div className="dash">
      <div className="dash__head">
        <div>
          <h2 className="dash__title" data-onb="app-header">Skida app — consumer usage</h2>
          <p className="dash__lede">
            Read-only over the consumer <code>users</code> and <code>PromoCodes</code> collections.
            Counts are computed server-side; no user records are loaded into the browser.
          </p>
        </div>
        <div className="dash__ranges">
          <span data-onb="app-filter">
            <RangeFilter value={range} onChange={setRange} />
          </span>
        </div>
      </div>

      {error && <ProductAccessError what="the app’s users" error={error} />}

      {!error && (
        <>
          {/* The journey, read left to right — arrive, try, buy, stay, leave —
              and the money it produced at the end of it.
              TRY is one column of two: the trials running now, split by whether
              auto-renewal is set to convert them or let them lapse. Replaced the
              event-based converted/cancelled pair on 25 Aug — the row is read to
              decide what to do next, and a trial set to cancel is still winnable
              in a way a trial that already lapsed is not. Both halves are scoped
              to the range like everything else here, so they sum to Trials
              running; the funnel below splits every live trial regardless of
              when it started, and each box's (i) says which it is.
              Gross USD sits at the far right rather than above: the row reads as
              a sentence, and the money is the end of it. */}
          <div className="dash__flow">
            {FLOW_STAGES.map((stage) => {
              const inStage = METRICS.filter((m) => m.stage === stage.key)
              if (inStage.length === 0) return null
              return (
                <div className="dash__flowgroup" key={stage.key}>
                  <div className="dash__flowlabel">{stage.label}</div>
                  <div
                    className={`dash__flowboxes${
                      inStage.length > 1 ? ' dash__flowboxes--stacked' : ''
                    }`}
                  >
                    {inStage.map((m) => {
                      const v = metrics?.[m.key] ?? null
                      const failed = metricErrors[m.key]
                      const spark = sparks?.[m.key] ?? null
                      return (
                        // A div holding two controls, not one button: the (i)
                        // is a button and a button inside a button is invalid,
                        // and the 14 Aug round asks for both — the box opens the
                        // chart, the (i) explains the figure without opening
                        // anything. A `title` had been carrying the explanation,
                        // which never appears on a touch screen.
                        <div className="dash__kpi" key={m.key}>
                          <button
                            className="dash__kpimain"
                            onClick={() => setOpenMetric(m)}
                            title={
                              failed
                                ? `${m.label} could not be counted. ${failed}`
                                : m.pointInTime
                                  ? `${m.label} — as of now, not for the selected range`
                                  : `${m.label} over ${rangeLabel.toLowerCase()}`
                            }
                          >
                            <div className="dash__kpilabel">{m.label}</div>
                            <div className="dash__kpirow">
                              {/* A failed query reads as a failed query. It used
                                  to render the same em-dash as a period with
                                  nothing in it, so a missing index looked like a
                                  quiet week — see `loadMetrics`. */}
                              <div
                                className={`dash__kpivalue${failed ? ' dash__kpivalue--failed' : ''}`}
                                style={failed ? undefined : { color: m.color }}
                              >
                                {metrics === null ? '…' : failed ? '!' : v === null ? '—' : nf(v)}
                              </div>
                              {spark && spark.some((p) => p > 0) && (
                                <Sparkline points={spark} color={m.color} label={m.label} />
                              )}
                            </div>
                            {/* Stacked boxes carry their help text in the (i)
                                instead — two of them side by side in one column
                                have room for a number, not a sentence. */}
                            {inStage.length > 1 ? (
                              <>
                                {m.eventsOnly && (
                                  <span className="dash__kpichip">since webhook</span>
                                )}
                                {/* This box does not answer to the range filter
                                    above it, and the chip is what stops somebody
                                    reading it as though it did. */}
                                {m.pointInTime && <span className="dash__kpichip">now</span>}
                              </>
                            ) : (
                              <div className="dash__kpisub">
                                {m.sub}
                                {m.eventsOnly && (
                                  <span className="dash__kpichip">since webhook</span>
                                )}
                                {m.pointInTime && <span className="dash__kpichip">now</span>}
                              </div>
                            )}
                          </button>
                          <span className="dash__kpiinfo">
                            <InfoTip label={m.label}>
                              {inStage.length > 1 && <p>{m.sub}.</p>}
                              <p>{m.basis}</p>
                              {m.caveat && <p>{m.caveat}</p>}
                            </InfoTip>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}

            {/* The end of the sentence the row reads as. Its own group so it
                keeps the label rhythm of the five before it. */}
            <div className="dash__flowgroup dash__flowgroup--money">
              <div className="dash__flowlabel">Money</div>
              <div className="dash__flowboxes">
                <div className="dash__kpi dash__gross">
                  <div className="dash__kpilabel">Gross USD</div>
                  <div className="dash__grossvalue">{gross === null ? '…' : `$${nf(gross)}`}</div>
                  <div className="dash__kpisub">{range.label}</div>
                  <span className="dash__kpiinfo">
                    <InfoTip label="Gross USD">
                      <p>
                        Gross revenue on subscriptions that started inside{' '}
                        {range.label.toLowerCase()}, from the RevenueCat mirror. App Store sandbox
                        records are excluded — 275 of them carry real-looking prices.
                      </p>
                      <p>
                        This is gross: no store commission, refunds or tax are deducted. What
                        reaches the bank is 30–45% lower, and that figure comes from the store
                        reports rather than from here.
                      </p>
                    </InfoTip>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/*
            Is our copy of the subscription data still current? — 26 Aug 2026.

            The original version of this compared our live-trial count against
            RevenueCat's and shouted whenever they differed. That was the wrong
            alarm. Ours is computed per subscription from their own API; theirs is
            an aggregate they recompute on their own schedule, and trials start
            and end continuously — so a gap of two or three is two vintages of
            the same truth, and a banner that fires on it every day is a banner
            nobody reads by the end of the week.

            What actually went wrong was **staleness**: the mirror had not been
            written to since 9 Aug, seventeen days, and eighteen documents still
            marked `trialing` had long expired. That is measurable directly, so it
            is what this warns on. The two totals are still shown side by side
            when they differ, quietly, because seeing them is useful and being
            told they disagree is not.

            Corrected 3 Sep 2026. "Measurable directly" was measured as the age
            of the newest document, which a webhook keeps at minutes for ever —
            it read 0.1 days old while 5,219 of 5,291 rows had not moved since
            26 Aug. It now counts the share of the collection that no full pass
            has re-confirmed, because a full pass writes every row and a webhook
            writes only the ones that changed. See `loadMirrorFreshness`.
          */}
          {(() => {
            /**
             * Null when either half could not be counted, rather than nought.
             *
             * `?? 0` would have turned a failed query into a confident zero and
             * put "RevenueCat counts 42 live trials and this row counts 0" on
             * screen — a made-up disagreement between a real number and a
             * missing one. The box itself already says it failed.
             */
            const ours =
              metrics === null ||
              metricErrors.trialsConverting ||
              metricErrors.trialsCancelling ||
              metrics.trialsConverting === null ||
              metrics.trialsCancelling === null
                ? null
                : metrics.trialsConverting + metrics.trialsCancelling
            const theirs = rcOverview?.activeTrials ?? null
            /**
             * Stale means "no full pass has run", and it is a share of the
             * collection rather than an age.
             *
             * A tenth: a pass may legitimately miss a handful of subscriptions
             * to a rate limit or a single API error, and a banner that fires on
             * three documents out of five thousand is a banner nobody reads by
             * the end of the week. A tenth of the collection is not a handful —
             * it is a pass that did not happen.
             */
            const share = mirror && mirror.total > 0 ? mirror.stale / mirror.total : 0
            const stale = share > 0.1
            if (!stale && (ours === null || theirs === null || ours === theirs)) return null
            return (
              <p className={`dash__drift${stale ? ' dash__drift--stale' : ''}`}>
                {stale ? (
                  <>
                    <strong>
                      {nf(mirror!.stale)} of the {nf(mirror!.total)} subscriptions that say they
                      are live have not been re-confirmed with RevenueCat in the last{' '}
                      {mirror!.withinDays} days.
                    </strong>{' '}
                    Everything in TRY and BUY is computed from that copy, so those figures describe
                    whenever each row was last heard about rather than today. The webhook only
                    reports subscriptions that <em>changed</em>, so it cannot correct a trial that
                    quietly lapsed while a delivery was failing — only a full pass can.{' '}
                    <code>crmRevenueCat</code> runs one daily, after 05:00; to force one now,{' '}
                    <code>scripts/backfillRevenueCat.ts --source customers</code>.
                  </>
                ) : (
                  <>
                    RevenueCat counts {nf(theirs!)} live trials and this row counts {nf(ours!)}. Both
                    are real: ours is counted per subscription from their API, theirs is an
                    aggregate they recompute periodically
                    {rcOverview?.fetchedAt && (
                      <> — read {formatDate(rcOverview.fetchedAt.slice(0, 10))}</>
                    )}
                    . The split by renewal intent can only come from our copy, because RevenueCat
                    publishes the total and not the breakdown.
                  </>
                )}
              </p>
            )
          })()}

          {/* Journey row → season goals → where the figures come from → access
              grants. The funnel, access mix, revenue, saved content and users
              table came off on 21 Oct 2026, asked for by the owner. */}
          <div className="micro dash__kpinote">
            Movement within <strong>{rangeLabel.toLowerCase()}</strong>, read left to right as a
            journey. The (i) says what a box counts; clicking the box draws the same figure over
            time, and the line inside it is that shape in miniature. The boxes marked{' '}
            <em>since webhook</em> measure transitions rather than state, so they only reach back to
            the day the webhook went live — the funnel below says which day.
          </div>



          <ProSubscribersCard />

          <PlacesCard />

          <span data-onb="app-season">
            <SeasonGoal />
          </span>

          <section className="dash__source">
            <div className="card-label">Where subscription figures come from</div>
            <p>
              <strong>Figures come from RevenueCat, and count subscriptions — not people.</strong>{' '}
              An older model let one account hold several at once, so subscriptions counted
              server-side can run above the number of people who have ever held one. Both are kept
              for a reason: <code>skidaCrmSubscriptions</code> answers “how many subscriptions”, the
              per-person rollup on <code>skidaCrmSubscribers</code> answers “how many people” —
              which is what the access mix needs.
            </p>
            <p>
              The <code>users.activeEntitlements</code> mirror is not used for any of this: it only
              refreshes when the app syncs, so it misses renewals from customers who haven’t opened
              Skida since.
            </p>
          </section>

          <div className="dash__grid dash__grid--single">
            <section className="card">
              <div className="card-label card-label--row">
                Access grants
                {isAdmin && (
                  <button className="btn btn--outline pcode__sm pcode__new" onClick={() => setCreatingCode(true)}>
                    New promo code
                  </button>
                )}
              </div>

              {grantsError && (
                <div className="empty-note dash__empty">Could not load grants: {grantsError}</div>
              )}

              {!grantsError && (
                <>
                  <div className="promo__head">
                    <div>Grant</div>
                    <div>Kind</div>
                    <div>Given</div>
                    <div>Active</div>
                    <div>Last used</div>
                  </div>
                  {(grants ?? []).map((g) => {
                    const rate = g.total > 0 ? Math.round((g.live / g.total) * 100) : null
                    // A promo code always opens: its header holds the link, the QR
                    // code and the edit form, used or not.
                    const clickable = g.people.length > 0 || g.kind === 'promo-code'
                    return (
                      <button
                        className={`promo__row${clickable ? ' promo__row--clickable' : ''}`}
                        key={g.id}
                        onClick={() => clickable && setOpenGrant(g)}
                        disabled={!clickable}
                        title={
                          g.kind === 'promo-code'
                            ? `Open ${g.label}: details, link and QR code`
                            : clickable
                              ? `Show who got access via ${g.label}`
                              : 'Nobody has used this yet'
                        }
                      >
                        <div className="promo__code">
                          {g.label}
                          {g.active === false && <span className="promo__off">inactive</span>}
                        </div>
                        <div className="muted promo__offering">
                          <span className={`grantkind grantkind--${g.kind}`}>
                            {g.kind === 'promo-code' ? 'code' : g.kind}
                          </span>
                          {g.detail && <span className="muted"> {g.detail}</span>}
                        </div>
                        <div className="muted">
                          {g.total}
                          {g.limit ? <span className="muted"> / {g.limit}</span> : null}
                        </div>
                        <div>
                          {g.total === 0 ? (
                            <span className="muted">—</span>
                          ) : (
                            <>
                              <span className={g.live > 0 ? 'promo__conv' : 'muted'}>{g.live}</span>
                              {rate !== null && <span className="muted"> ({rate}%)</span>}
                            </>
                          )}
                        </div>
                        <div className="muted">{g.lastAt ? formatDate(g.lastAt) : '—'}</div>
                      </button>
                    )
                  })}
                  {grants === null && <div className="empty-note dash__empty">Loading…</div>}
                  {grants?.length === 0 && (
                    <div className="empty-note dash__empty">No grants recorded yet.</div>
                  )}
                  {grantTotals && (
                    <div className="micro promo__note">
                      {nf(grantTotals.given)} grants given · {nf(grantTotals.live)} still active ·{' '}
                      {grantTotals.used} sources used.
                    </div>
                  )}
                  <div className="micro promo__note">
                    Every route to Premium that is not a full-price purchase: redeemed promo codes,
                    RevenueCat granted entitlements (which is how the FriFlyt arrangement and any
                    hand-granted access arrive), and partner programmes. Click a row to see who.
                    “Active” counts the people whose entitlement has not expired.
                  </div>
                </>
              )}
            </section>
          </div>


          <section className="card dash__caveat">
            <div className="card-label">Not built — and why</div>
            <ul className="dash__todo">
              <li>
                <strong>Weekly active logins, and last sign-in per user.</strong> Both are the
                same gap: the user document carries no sign-in timestamp at all. Firebase Auth
                keeps <code>lastSignInTime</code>, and only the Admin SDK can read it — so there is
                neither a current value to show in the users table nor a history to bucket into a
                weekly series. A Cloud Function mirroring it onto the user, or a login event log,
                fills in both at once.
              </li>
              <li>
                <strong>Churn before the webhook.</strong> Everything from{' '}
                {feed && feed.events > 0 ? 'the first live event onwards' : 'the webhook going live'}{' '}
                is reconstructible — <code>skidaCrmSubscriptionEvents</code> is append-only and
                keeps cancellations, expirations and renewals as they happen. What cannot be
                recovered is the period before that: the backfill captured each subscription's
                current state, not its history, so a churn curve can only start from the day the
                webhook did.
              </li>
              <li>
                <strong>Beware <code>activeEntitlements.Premium.isActive</code>.</strong> It is
                written once and never cleared: {nf(data?.premiumActive ?? 0)} +{' '}
                {nf(data?.premiumLapsed ?? 0)} = every account that has <em>ever</em> held Premium
                reads <code>true</code>, and none reads <code>false</code>. Anything counting on it
                overstates active subscribers by the lapsed figure. This dashboard uses{' '}
                <code>expirationDateMillis</code>.
              </li>
            </ul>
          </section>
        </>
      )}

      {openGrant && (
        <GrantPeopleModal
          grant={openGrant}
          onClose={() => setOpenGrant(null)}
          canEdit={isAdmin}
          onChanged={() => setGrantsVersion((v) => v + 1)}
        />
      )}

      {creatingCode && (
        <Modal
          title="New promo code"
          subtitle="Written to PromoCodes in the Skida App project, through the backend"
          width={640}
          dismiss="explicit"
          onClose={() => setCreatingCode(false)}
          footer={null}
        >
          <PromoCodeForm
            onCancel={() => setCreatingCode(false)}
            onSaved={(code) => {
              setCreatingCode(false)
              setGrantsVersion((v) => v + 1)
              // Open the new code, so its link and QR code are right there.
              setOpenGrant({
                kind: 'promo-code',
                id: code,
                label: code,
                detail: '',
                total: 0,
                live: 0,
                lastAt: null,
                people: [],
              })
            }}
          />
        </Modal>
      )}

      {openMetric && (
        <MetricChartModal
          metric={openMetric}
          window={window}
          rangeLabel={rangeLabel}
          onClose={() => setOpenMetric(null)}
        />
      )}
    </div>
  )
}
